import { getIdToken } from "./auth";
import { ACTIVE_ORG_STORAGE_KEY, ORG_ID_HEADER } from "./constants";

export interface ApiClientConfig {
  baseUrl: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

export interface ApiClient {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body: unknown) => Promise<T>;
  put: <T>(path: string, body: unknown) => Promise<T>;
  patch: <T>(path: string, body: unknown) => Promise<T>;
  del: <T>(path: string, body?: unknown) => Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly requestId: string | null = null,
    /**
     * The service's typed failure code (`error.code`), when it sent one.
     *
     * Carried because a status alone cannot separate the failures a caller has
     * to word differently: the entitlements bundle routes answer 422 for both a
     * definition that no longer validates and one too large to apply
     * atomically, which are different things to tell an operator. Null when the
     * response carried no code, which includes every unhandled 500.
     */
    public readonly code: string | null = null,
    /**
     * The rest of the service's typed `error` object, minus the fields above.
     *
     * A route whose failure carries state (which status a request is in, and
     * who put it there) has nowhere else to put it: promoting a field per
     * route would grow this class for every caller, and dropping them leaves
     * a console able to say only "conflict". Untyped on purpose, because the
     * shape is the ROUTE's contract and belongs to whoever reads it; null when
     * the response carried no structured error object.
     */
    public readonly details: Readonly<Record<string, unknown>> | null = null
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const authHeaders = await config.getAuthHeaders();
    const hasBody = body !== undefined;
    const init: RequestInit = {
      method,
      headers: hasBody
        ? { "content-type": "application/json", ...authHeaders }
        : { ...authHeaders },
    };
    if (hasBody) {
      init.body = JSON.stringify(body);
    }
    const response = await fetch(`${config.baseUrl}${path}`, init);

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      if (!response.ok) {
        throw new ApiError(response.status, `Request failed with status ${response.status}`);
      }
      throw new ApiError(response.status, "Response was not valid JSON");
    }

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      let requestId: string | null = null;
      let code: string | null = null;
      let details: Readonly<Record<string, unknown>> | null = null;
      if (data !== null && typeof data === "object" && "error" in data) {
        const err = (data as Record<string, unknown>).error;
        if (typeof err === "string") {
          errorMessage = err;
        } else if (err !== null && typeof err === "object") {
          const msg = (err as Record<string, unknown>).message;
          const requestIdValue = (err as Record<string, unknown>).request_id;
          const codeValue = (err as Record<string, unknown>).code;
          if (typeof msg === "string") {
            errorMessage = msg;
          }
          if (typeof requestIdValue === "string") {
            requestId = requestIdValue;
          }
          if (typeof codeValue === "string") {
            code = codeValue;
          }
          /* A NULL-prototype bag, because these keys come off the wire. With
             a normal prototype a lookup for a field the service omitted falls
             through to Object.prototype and answers with something real, so an
             absent field would read as present. Null prototype makes absent
             mean absent. (This is not a prototype-pollution fix: nothing here
             assigns, so a `__proto__` key was only ever an own property.) */
          const promoted = ["message", "request_id", "code"];
          const bag = Object.create(null) as Record<string, unknown>;
          for (const [field, value] of Object.entries(err as Record<string, unknown>)) {
            if (promoted.includes(field)) continue;
            Object.defineProperty(bag, field, {
              value,
              enumerable: true,
              writable: true,
              configurable: true,
            });
          }
          details = bag;
        }
      }
      throw new ApiError(response.status, errorMessage, requestId, code, details);
    }

    return data as T;
  }

  return {
    get: <T>(path: string): Promise<T> => request<T>("GET", path),
    post: <T>(path: string, body: unknown): Promise<T> => request<T>("POST", path, body),
    put: <T>(path: string, body: unknown): Promise<T> => request<T>("PUT", path, body),
    patch: <T>(path: string, body: unknown): Promise<T> => request<T>("PATCH", path, body),
    del: <T>(path: string, body?: unknown): Promise<T> => request<T>("DELETE", path, body),
  };
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  if (!token) {
    return {};
  }
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  try {
    const orgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    if (orgId) {
      headers[ORG_ID_HEADER] = orgId;
    }
  } catch {
    // localStorage may be unavailable in restricted-storage environments
  }
  return headers;
}

export const apiClient = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? "",
  getAuthHeaders,
});
